import { Badge, theme } from 'antd';
import {
  Beef,
  Boxes,
  Building2,
  CalendarDays,
  ChefHat,
  CupSoda,
  Factory,
  FlagTriangleRightIcon,
  Gauge,
  Group,
  LeafyGreen,
  PackagePlus,
  ReceiptText,
  Salad,
  Settings,
  Shapes,
  Sparkles,
  Store,
  Truck,
  Utensils,
  Weight,
} from 'lucide-react';
import { type ReactElement, type ReactNode, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import AuthorizedComponent from '../components/authorized-component';
import { useCartContext } from '../contexts/cart.context';
import InvoicesPage from '../pages/billing/invoices/invoices.page';
import { DashboardPage } from '../pages/general/dashboard.page';
import CustomersPage from '../pages/management/customers/customers.page';
import OrganizationsPage from '../pages/management/organizations/organizations.page';
import ProductionLinesPage from '../pages/management/production-lines/production-lines.page';
import MasterSettingsPage from '../pages/management/settings/master-settings.page';
import IngredientsPage from '../pages/meals-planning/ingredients/ingredients.page';
import MealItemsPage from '../pages/meals-planning/meal-items/meal-items.page';
import MealsPage from '../pages/meals-planning/meals/meals.page';
import SpecialEventMealsPage from '../pages/meals-planning/special-event-meals/special-event-meals.page';
import BeveragesAndSuppliesPage from '../pages/meals-settings/beverages-and-supplies/beverages-and-supplies.page';
import FoodGroupsPage from '../pages/meals-settings/food-groups/food-groups.page';
import MealCategoriesPage from '../pages/meals-settings/meal-categories/meal-categories.page';
import MeasuresPage from '../pages/meals-settings/measures/measures.page';
import ServingToolsPage from '../pages/meals-settings/serving-tools/serving-tools.page';
import CustomerOrderFormPage from '../pages/orders/customer-orders/customer-order-form/customer-order-form.page';
import CustomerOrderUpdateFormPage from '../pages/orders/customer-orders/customer-order-form/customer-order-update-form.page';
import CustomerOrdersPage from '../pages/orders/customer-orders/customer-orders.page';
import MenusPage from '../pages/orders/menus/menus.page';
import DeliveryReceiptsPage from '../pages/reports/delivery-receipts/delivery-receipts.page';
import PrepSheetsConfigurationsPage from '../pages/reports/prep-sheets/prep-sheets-configuration.page';
import PrepSheetsPage from '../pages/reports/prep-sheets/prep-sheets.page';
import ProductionLinesReportPage from '../pages/reports/production-lines-report/production-lines-report.page';
import CycleMenuSchedulesPage from '../pages/scheduling/cycle-menus/cycle-menu-schedules.page';
import CycleMenusPage from '../pages/scheduling/cycle-menus/cycles-menu.page';
import { Roles } from '../types';

interface PageType {
  name: string;
  group: string;
  icon: ReactNode;
  extra?: ReactNode;
  roles?: Roles[];
  element?: ReactElement;
  children?: any[];
}
const usePages = (): Record<string, PageType> => {
  const { token } = theme.useToken();
  const cartContext = useCartContext();

  return useMemo(
    () => ({
      '/': {
        name: 'Dashboard',
        group: 'general',
        icon: <Gauge />,
        // roles: [OrganizationRoles.Standard, OrganizationRoles.Administrator],
        element: (
          <AuthorizedComponent>
            <DashboardPage />
          </AuthorizedComponent>
        ),
      },
      // '/profile': {
      //   name: 'My profile',
      //   group: 'user',
      //   icon: <CircleUserRound />,
      //   // roles: [OrganizationRoles.Standard, OrganizationRoles.Administrator],
      //   element: (
      //     <AuthorizedComponent>
      //       <div>Profile - Not implemented!</div>
      //     </AuthorizedComponent>
      //   ),
      // },
      '/new-order': {
        name: 'New order',
        extra:
          cartContext.itemsCount > 0 ? (
            <Badge
              dot
              style={{ marginInlineEnd: token.marginSM, marginBlockStart: token.marginXXS }}
            />
          ) : undefined,
        group: 'orders',
        icon: <PackagePlus />,
        roles: [Roles.ROLE_HHC_ADMIN, Roles.ROLE_CUSTOMER_ADMIN, Roles.ROLE_CUSTOMER_STANDARD],
        element: (
          <AuthorizedComponent>
            <CustomerOrderFormPage />
          </AuthorizedComponent>
        ),
      },
      '/update-order/:orderId': {
        name: 'Update order',
        group: '',
        icon: <PackagePlus />,
        roles: [Roles.ROLE_HHC_ADMIN, Roles.ROLE_CUSTOMER_ADMIN, Roles.ROLE_CUSTOMER_STANDARD],
        element: (
          <AuthorizedComponent>
            <CustomerOrderUpdateFormPage />
          </AuthorizedComponent>
        ),
      },
      // '/new-special-event-order': {
      //   name: 'New special event orders',
      //   group: 'orders',
      //   icon: <Sparkles />,
      //   roles: [Roles.ROLE_HHC_ADMIN, Roles.ROLE_CUSTOMER_ADMIN],
      //   element: (
      //     <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN, Roles.ROLE_CUSTOMER_ADMIN]}>
      //       <SpecialEventAvailableComponent>
      //         <CustomerSpecialEventOrderFormPage />
      //       </SpecialEventAvailableComponent>
      //     </AuthorizedComponent>
      //   ),
      // },
      '/customers': {
        name: 'Customers',
        group: 'management',
        icon: <Store />,
        roles: [Roles.ROLE_HHC_ADMIN],
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <CustomersPage />
          </AuthorizedComponent>
        ),
      },
      '/my-customer': {
        name: 'My Customer',
        group: 'management',
        icon: <Store />,
        roles: [Roles.ROLE_CUSTOMER_ADMIN],
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_CUSTOMER_ADMIN]}>
            <CustomersPage />
          </AuthorizedComponent>
        ),
      },
      '/orders': {
        name: 'Orders',
        group: 'orders',
        icon: <Boxes />,
        roles: [Roles.ROLE_HHC_ADMIN, Roles.ROLE_CUSTOMER_ADMIN, Roles.ROLE_CUSTOMER_STANDARD],
        element: <CustomerOrdersPage />,
      },
      '/menus': {
        name: 'Menus',
        group: 'orders',
        icon: <Utensils />,
        roles: [Roles.ROLE_HHC_ADMIN, Roles.ROLE_CUSTOMER_ADMIN, Roles.ROLE_CUSTOMER_STANDARD],
        element: (
          <AuthorizedComponent>
            <MenusPage />
          </AuthorizedComponent>
        ),
      },
      '/delivery-receipts': {
        name: 'Delivery receipts',
        group: 'reports',
        icon: <Truck />,
        element: <DeliveryReceiptsPage />,
      },
      '/production-line-report': {
        name: 'Production Line Report',
        group: 'reports',
        icon: <FlagTriangleRightIcon />,
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <ProductionLinesReportPage />
          </AuthorizedComponent>
        ),
      },
      '/prep-sheets': {
        name: 'Preparation Sheets',
        group: 'reports',
        icon: <ChefHat />,
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <PrepSheetsPage />
          </AuthorizedComponent>
        ),
      },
      '/prep-sheets-configuration': {
        name: 'Prep Sheets Configurations',
        group: 'reports',
        icon: <ChefHat />,
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <PrepSheetsConfigurationsPage />
          </AuthorizedComponent>
        ),
      },
      '/invoices': {
        name: 'Invoices',
        group: 'billing',
        icon: <ReceiptText />,
        roles: [Roles.ROLE_HHC_ADMIN, Roles.ROLE_CUSTOMER_ADMIN],
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN, Roles.ROLE_CUSTOMER_ADMIN]}>
            <InvoicesPage />
          </AuthorizedComponent>
        ),
      },
      '/meal-categories': {
        name: 'Meal Categories',
        group: 'meals-settings',
        icon: <Group />,
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <MealCategoriesPage />
          </AuthorizedComponent>
        ),
      },
      '/special-event-meals': {
        name: 'Special Event Meals',
        group: 'planning',
        icon: <Sparkles />,
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <SpecialEventMealsPage />
          </AuthorizedComponent>
        ),
      },
      '/meals': {
        name: 'Meals',
        group: 'planning',
        icon: <Beef />,
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN, Roles.ROLE_CUSTOMER_STANDARD]}>
            <MealsPage />
          </AuthorizedComponent>
        ),
      },
      '/meal-items': {
        name: 'Meal Items',
        group: 'planning',
        icon: <Salad />,
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <MealItemsPage />
          </AuthorizedComponent>
        ),
      },
      '/ingredients': {
        name: 'Ingredients',
        group: 'planning',
        icon: <LeafyGreen />,
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <IngredientsPage />
          </AuthorizedComponent>
        ),
      },
      '/food-groups': {
        name: 'Food Groups',
        group: 'meals-settings',
        icon: <Shapes />,
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <FoodGroupsPage />
          </AuthorizedComponent>
        ),
      },
      '/cycle-menus': {
        name: 'Cycle Menus',
        group: 'scheduling',
        icon: <CalendarDays />,
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <Outlet />
          </AuthorizedComponent>
        ),
        children: [
          {
            path: '',
            element: <CycleMenusPage />,
          },
          {
            path: ':cycleMenuId',
            name: ':cycleMenuName',
            element: <CycleMenuSchedulesPage />,
          },
        ],
      },
      // '/client-tags': {
      //   name: 'Tags',
      //   group: 'management',
      //   icon: <Tags />,
      //   // roles: [OrganizationRoles.Standard, OrganizationRoles.Administrator],
      //   element: (
      //     <AuthorizedComponent roles={[Roles.ROLE_Admin]}>
      //       <ProductionLinesPage />
      //     </AuthorizedComponent>
      //   ),
      // },
      '/master-settings': {
        name: 'Master Settings',
        group: 'management',
        icon: <Settings />,
        // roles: [OrganizationRoles.Standard, OrganizationRoles.Administrator],
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <MasterSettingsPage />
          </AuthorizedComponent>
        ),
      },
      '/beverages-supplies': {
        name: 'Beverages and supplies',
        group: 'meals-settings',
        icon: <CupSoda />,
        // roles: [OrganizationRoles.Standard, OrganizationRoles.Administrator],
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <BeveragesAndSuppliesPage />
          </AuthorizedComponent>
        ),
      },
      '/measures': {
        name: 'Measures',
        group: 'meals-settings',
        icon: <Weight />,
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <MeasuresPage />
          </AuthorizedComponent>
        ),
      },
      '/serving-tools': {
        name: 'Serving Tools',
        group: 'meals-settings',
        icon: <Utensils />,
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <ServingToolsPage />
          </AuthorizedComponent>
        ),
      },
      '/production-lines': {
        name: 'Production Lines',
        group: 'meals-settings',
        icon: <Factory />,
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <ProductionLinesPage />
          </AuthorizedComponent>
        ),
      },
      '/organizations': {
        name: 'Organizations',
        group: 'management',
        icon: <Building2 />,
        // roles: [OrganizationRoles.Standard, OrganizationRoles.Administrator],
        element: (
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <OrganizationsPage />
          </AuthorizedComponent>
        ),
      },
    }),
    [cartContext.itemsCount]
  );
};

export default usePages;
